import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wifi,
  Bell,
  Wallet,
  Key,
  X,
  Navigation,
  ClipboardCheck,
  Share2,
  Cloud,
  Sun,
  Droplets,
  Wind,
  Thermometer,
} from 'lucide-react';
import {
  WiFiCard,
  HostContactCard,
  SelfCheckInCard,
  ArrivalNavigationCard,
} from '@/components/guest';
import {
  DEMO_WIFI,
  DEMO_PARKING,
  DEMO_SELF_CHECKIN,
  DEMO_CHECK_TIMES,
  DEMO_PROPERTY,
  DEMO_LANDLORD,
  DEMO_WEATHER,
  DEMO_GROUP,
  getLocalizedText,
} from '@/data/demoMockData';

type ModalId =
  | 'wifi'
  | 'notifications'
  | 'navigation'
  | 'payment'
  | 'arrival'
  | 'onlineCheckin'
  | 'share'
  | 'weather';

interface DemoQuickActionsGridProps {
  darkMode: boolean;
  lang: 'hr' | 'en';
  highlightCard?: ModalId | null;
  /** When set externally, opens that modal */
  forceOpenModal?: ModalId | null;
  onModalChange?: (modal: ModalId | null) => void;
}

export function DemoQuickActionsGrid({
  darkMode,
  lang,
  highlightCard,
  forceOpenModal,
  onModalChange,
}: DemoQuickActionsGridProps) {
  const { t } = useTranslation();
  const [activeModal, setActiveModal] = useState<ModalId | null>(null);

  // External control
  useEffect(() => {
    if (forceOpenModal !== undefined && forceOpenModal !== activeModal) {
      setActiveModal(forceOpenModal);
    }
  }, [forceOpenModal]);

  useEffect(() => {
    onModalChange?.(activeModal);
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal, onModalChange]);

  const cards = useMemo(
    () => [
      {
        id: 'wifi' as const,
        icon: Wifi,
        title: t('guest.quickActions.wifi', 'WiFi pristup'),
        gradient: 'from-cyan-500 to-blue-600',
      },
      {
        id: 'notifications' as const,
        icon: Bell,
        title: t('guest.quickActions.notifications', 'Obavijesti domaćina'),
        gradient: 'from-emerald-500 to-teal-600',
      },
      {
        id: 'navigation' as const,
        icon: Navigation,
        title: t('guest.quickActions.navigation', 'Navigacija'),
        gradient: 'from-orange-500 to-amber-600',
      },
      {
        id: 'payment' as const,
        icon: Wallet,
        title: t('guest.quickActions.payment', 'Plaćanje'),
        gradient: 'from-rose-500 to-pink-600',
      },
      {
        id: 'arrival' as const,
        icon: Key,
        title: t('guest.quickActions.arrival', 'Dolazak i odlazak'),
        gradient: 'from-violet-500 to-purple-600',
      },
      {
        id: 'onlineCheckin' as const,
        icon: ClipboardCheck,
        title: t('guest.quickActions.onlineCheckin', 'Online Check-in'),
        gradient: 'from-indigo-500 to-purple-600',
      },
      {
        id: 'share' as const,
        icon: Share2,
        title: t('guest.quickActions.share', 'Podijeli s putnicima'),
        gradient: 'from-sky-500 to-cyan-600',
      },
    ],
    [t]
  );

  const renderModalContent = () => {
    switch (activeModal) {
      case 'wifi':
        return (
          <WiFiCard
            networkName={DEMO_WIFI.networkName}
            password={DEMO_WIFI.password}
            darkMode={darkMode}
          />
        );
      case 'notifications':
        return <DemoNotifyHostContent darkMode={darkMode} lang={lang} />;
      case 'navigation':
        return (
          <ArrivalNavigationCard
            propertyAddress={DEMO_PROPERTY.address}
            propertyCity={DEMO_PROPERTY.city}
            propertyLatitude={DEMO_PROPERTY.latitude}
            propertyLongitude={DEMO_PROPERTY.longitude}
            parkingAvailable={DEMO_PARKING.available}
            parkingType={DEMO_PARKING.type}
            parkingFree={DEMO_PARKING.free}
            parkingLatitude={DEMO_PARKING.latitude}
            parkingLongitude={DEMO_PARKING.longitude}
            parkingAddress={DEMO_PARKING.address}
            parkingInstructions={getLocalizedText(DEMO_PARKING.instructions, lang)}
            parkingAfterArrival={getLocalizedText(DEMO_PARKING.afterArrival, lang)}
            parkingSpots={DEMO_PARKING.spots}
            darkMode={darkMode}
          />
        );
      case 'payment':
        return <DemoPaymentContent darkMode={darkMode} lang={lang} />;
      case 'arrival':
        return (
          <SelfCheckInCard
            checkInTime={DEMO_CHECK_TIMES.checkInTime}
            selfCheckinEnabled={DEMO_SELF_CHECKIN.enabled}
            checkinMethod={DEMO_SELF_CHECKIN.method}
            lockboxLocation={getLocalizedText(DEMO_SELF_CHECKIN.lockboxLocation, lang)}
            lockboxCode={DEMO_SELF_CHECKIN.lockboxCode}
            keypadCode={null}
            smartLockProvider={null}
            smartLockInstructions={null}
            keyLocation={null}
            entranceInstructions={null}
            doorCode={null}
            gateCode={null}
            buildingAccessInfo={null}
            floorApartmentInfo={null}
            checkOutTime={DEMO_CHECK_TIMES.checkOutTime}
            darkMode={darkMode}
          />
        );
      case 'onlineCheckin':
        return <DemoOnlineCheckinContent darkMode={darkMode} lang={lang} />;
      case 'share':
        return <DemoShareContent darkMode={darkMode} lang={lang} />;
      case 'weather':
        return <DemoWeatherContent darkMode={darkMode} lang={lang} />;
      default:
        return null;
    }
  };

  const activeCard = cards.find((c) => c.id === activeModal);

  return (
    <>
      {/* Grid of Quick Action Cards */}
      <div className="grid grid-cols-2 gap-3 px-4 mt-6 mb-6">
        {cards.map((card) => {
          const Icon = card.icon;
          const isHighlighted = highlightCard === card.id;
          return (
            <button
              key={card.id}
              data-demo-card={card.id}
              onClick={() => setActiveModal(card.id)}
              className={`group relative overflow-hidden rounded-2xl p-6 transition-all duration-500 hover:scale-[1.02] active:scale-95 ${
                darkMode
                  ? 'bg-slate-800/80 border-2 border-slate-700/50 hover:border-slate-600/70'
                  : 'bg-white/80 border-2 border-gray-200/50 hover:border-gray-300/70 shadow-xl hover:shadow-2xl'
              } backdrop-blur-md ${
                isHighlighted
                  ? 'ring-4 ring-cyan-400 ring-offset-4 ring-offset-transparent shadow-2xl shadow-cyan-500/50 scale-[1.03]'
                  : ''
              }`}
            >
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="relative">
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div
                    className={`absolute inset-0 w-16 h-16 rounded-2xl bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-40 blur-xl transition-opacity duration-500`}
                  />
                </div>
                <h3
                  className={`text-sm font-bold text-center leading-tight ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {card.title}
                </h3>
              </div>
            </button>
          );
        })}
      </div>

      {/* Modal - absolute (fits inside phone frame) */}
      {activeModal && activeCard && (
        <div
          className={`absolute inset-0 z-[60] flex flex-col overflow-hidden animate-in fade-in duration-200 ${
            darkMode
              ? 'bg-gradient-to-b from-slate-900 via-blue-950 to-slate-950'
              : 'bg-gradient-to-b from-sky-100 via-blue-50 to-white'
          }`}
        >
          {/* Header */}
          <div
            className={`shrink-0 relative overflow-hidden border-b ${
              darkMode ? 'border-slate-700/50' : 'border-gray-200/50'
            }`}
          >
            <div
              className={`absolute inset-0 ${
                darkMode
                  ? 'bg-gradient-to-br from-slate-800/95 via-blue-900/90 to-slate-900/85'
                  : 'bg-gradient-to-br from-white/95 via-blue-50/90 to-sky-100/85'
              }`}
            />
            <div
              className={`absolute inset-0 bg-gradient-to-r ${activeCard.gradient} ${
                darkMode ? 'opacity-25' : 'opacity-15'
              }`}
            />
            <div className="relative flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div
                  className={`relative w-11 h-11 rounded-2xl bg-gradient-to-br ${activeCard.gradient} flex items-center justify-center shadow-xl ring-2 ring-white/20`}
                >
                  <activeCard.icon className="w-5 h-5 text-white drop-shadow" />
                </div>
                <h2
                  className={`text-lg font-bold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {activeCard.title}
                </h2>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2.5 rounded-xl transition-all ${
                  darkMode
                    ? 'bg-slate-800/50 hover:bg-slate-700 text-slate-300 border border-slate-700/50'
                    : 'bg-white/80 hover:bg-gray-100 text-gray-700 border border-gray-200'
                }`}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            className={`flex-1 min-h-0 overflow-y-auto p-4 pb-6 animate-in slide-in-from-bottom-4 duration-300 ${
              darkMode
                ? 'bg-gradient-to-b from-slate-900/50 to-blue-950/50'
                : 'bg-gradient-to-b from-blue-50/50 to-sky-50/50'
            }`}
          >
            {renderModalContent()}
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// COMPACT WEATHER BANNER (separate trigger card above the grid)
// ============================================================

interface DemoWeatherBannerProps {
  darkMode: boolean;
  lang: 'hr' | 'en';
  onOpen: () => void;
}

export function DemoWeatherBanner({ darkMode, lang, onOpen }: DemoWeatherBannerProps) {
  const w = DEMO_WEATHER.current;

  return (
    <button
      onClick={onOpen}
      className={`w-[calc(100%-2rem)] mx-4 mb-4 px-3 py-2.5 rounded-2xl backdrop-blur-lg transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-lg hover:shadow-xl ${
        darkMode
          ? 'bg-gradient-to-r from-slate-800/80 via-blue-900/70 to-slate-800/80 border border-slate-700/50'
          : 'bg-gradient-to-r from-white/90 via-sky-50/90 to-white/90 border border-blue-200/50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
            <Sun className="w-4 h-4 text-white" />
          </div>
          <div className="flex flex-col text-left">
            <span
              className={`text-xl font-bold leading-tight ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {w.temperature}°C
            </span>
            <span
              className={`text-[10px] leading-tight ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              {getLocalizedText(w.description, lang)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <div
            className={`flex items-center gap-1 ${
              darkMode ? 'text-slate-300' : 'text-gray-600'
            }`}
          >
            <Droplets className="w-3.5 h-3.5" />
            {w.humidity}%
          </div>
          <div
            className={`flex items-center gap-1 ${
              darkMode ? 'text-slate-300' : 'text-gray-600'
            }`}
          >
            <Wind className="w-3.5 h-3.5" />
            {w.windSpeed}km/h
          </div>
        </div>
      </div>
    </button>
  );
}

// ============================================================
// MODAL CONTENTS
// ============================================================

function DemoWeatherContent({ darkMode, lang }: { darkMode: boolean; lang: 'hr' | 'en' }) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${
        darkMode
          ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100'
      }`}
    >
      <div className="absolute top-2 right-3 w-16 h-16 bg-yellow-300/20 rounded-full blur-xl" />
      <div className="relative p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{DEMO_WEATHER.current.icon}</span>
            <div>
              <p
                className={`text-3xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {DEMO_WEATHER.current.temperature}°C
              </p>
              <p
                className={`text-sm ${
                  darkMode ? 'text-slate-400' : 'text-gray-500'
                }`}
              >
                {getLocalizedText(DEMO_WEATHER.current.description, lang)}
              </p>
            </div>
          </div>
          <div className="text-right space-y-1">
            <div
              className={`flex items-center gap-1.5 text-xs justify-end ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              <Droplets className="w-3.5 h-3.5" />
              <span>{DEMO_WEATHER.current.humidity}%</span>
            </div>
            <div
              className={`flex items-center gap-1.5 text-xs justify-end ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              <Wind className="w-3.5 h-3.5" />
              <span>{DEMO_WEATHER.current.windSpeed} km/h</span>
            </div>
            <div
              className={`flex items-center gap-1.5 text-xs justify-end ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              <Thermometer className="w-3.5 h-3.5" />
              <span>UV {DEMO_WEATHER.current.uvIndex}</span>
            </div>
          </div>
        </div>

        <div
          className={`flex gap-2 pt-3 border-t ${
            darkMode ? 'border-slate-700/50' : 'border-sky-200/60'
          }`}
        >
          {DEMO_WEATHER.forecast.map((day, i) => (
            <div
              key={i}
              className={`flex-1 text-center py-2 rounded-xl ${
                darkMode ? 'bg-slate-700/30' : 'bg-white/60'
              }`}
            >
              <p
                className={`text-[10px] font-medium mb-1 ${
                  darkMode ? 'text-slate-400' : 'text-gray-500'
                }`}
              >
                {getLocalizedText(day.day, lang)}
              </p>
              <span className="text-lg">{day.icon}</span>
              <div className="mt-1">
                <span
                  className={`text-xs font-bold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {day.high}°
                </span>
                <span
                  className={`text-[10px] ml-0.5 ${
                    darkMode ? 'text-slate-500' : 'text-gray-400'
                  }`}
                >
                  {day.low}°
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DemoNotifyHostContent({ darkMode, lang }: { darkMode: boolean; lang: 'hr' | 'en' }) {
  const actions = [
    {
      key: 'on_way',
      labelHr: "Krenuo sam",
      labelEn: "I'm on my way",
      gradient: 'from-blue-500 to-cyan-600',
    },
    {
      key: 'parking',
      labelHr: 'Na parkingu sam',
      labelEn: 'At parking',
      gradient: 'from-orange-500 to-amber-600',
    },
    {
      key: 'arrived',
      labelHr: 'Stigao sam',
      labelEn: "I've arrived",
      gradient: 'from-emerald-500 to-green-600',
    },
    {
      key: 'share_location',
      labelHr: 'Podijeli lokaciju',
      labelEn: 'Share location',
      gradient: 'from-violet-500 to-purple-600',
    },
  ];

  return (
    <div
      className={`rounded-2xl p-5 ${
        darkMode
          ? 'bg-slate-800/50 border border-slate-700/50'
          : 'bg-white border border-gray-100 shadow-xl'
      }`}
    >
      <div className="grid grid-cols-2 gap-3">
        {actions.map((a) => (
          <div
            key={a.key}
            className={`relative rounded-2xl p-5 text-white bg-gradient-to-br ${a.gradient} shadow-xl`}
          >
            <Bell className="w-7 h-7 mb-2" />
            <p className="font-bold text-sm">{lang === 'hr' ? a.labelHr : a.labelEn}</p>
          </div>
        ))}
      </div>
      <p
        className={`text-xs text-center mt-4 ${
          darkMode ? 'text-slate-400' : 'text-gray-500'
        }`}
      >
        {lang === 'hr'
          ? 'Domaćin dobiva push notifikaciju u sekundi'
          : 'Host gets a push notification instantly'}
      </p>
    </div>
  );
}

function DemoPaymentContent({ darkMode, lang }: { darkMode: boolean; lang: 'hr' | 'en' }) {
  const paid = DEMO_GROUP.totalPaid;
  const total = DEMO_GROUP.totalPrice;
  const pct = (paid / total) * 100;

  return (
    <div className="space-y-4">
      <div
        className={`rounded-2xl p-5 ${
          darkMode
            ? 'bg-slate-800/50 border border-slate-700/50'
            : 'bg-white border border-gray-100 shadow-xl'
        }`}
      >
        <div className="flex justify-between items-baseline mb-3">
          <span
            className={`text-sm font-medium ${
              darkMode ? 'text-slate-400' : 'text-gray-500'
            }`}
          >
            {lang === 'hr' ? 'Ukupno' : 'Total'}
          </span>
          <span
            className={`text-2xl font-bold ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            {total}€
          </span>
        </div>
        <div
          className={`h-3 rounded-full overflow-hidden ${
            darkMode ? 'bg-slate-700' : 'bg-gray-100'
          }`}
        >
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-green-600 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs">
          <span className="text-emerald-500 font-semibold">
            {lang === 'hr' ? 'Plaćeno' : 'Paid'}: {paid}€
          </span>
          <span
            className={
              darkMode ? 'text-amber-400 font-semibold' : 'text-amber-600 font-semibold'
            }
          >
            {lang === 'hr' ? 'Preostalo' : 'Remaining'}: {total - paid}€
          </span>
        </div>
      </div>

      <div
        className={`rounded-2xl p-5 ${
          darkMode
            ? 'bg-slate-800/50 border border-slate-700/50'
            : 'bg-white border border-gray-100 shadow-xl'
        }`}
      >
        <h4
          className={`font-bold text-sm mb-3 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}
        >
          {lang === 'hr' ? 'Načini plaćanja' : 'Payment methods'}
        </h4>
        <div className="space-y-2">
          {[
            { icon: '💵', label: lang === 'hr' ? 'Gotovina pri dolasku' : 'Cash on arrival' },
            { icon: '🏦', label: lang === 'hr' ? 'Bankovni prijenos' : 'Bank transfer' },
            { icon: '💳', label: lang === 'hr' ? 'Kartica' : 'Card' },
          ].map((m, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 p-3 rounded-xl ${
                darkMode ? 'bg-slate-700/30' : 'bg-gray-50'
              }`}
            >
              <span className="text-2xl">{m.icon}</span>
              <span
                className={`text-sm ${
                  darkMode ? 'text-slate-200' : 'text-gray-700'
                }`}
              >
                {m.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DemoOnlineCheckinContent({
  darkMode,
  lang,
}: {
  darkMode: boolean;
  lang: 'hr' | 'en';
}) {
  return (
    <div
      className={`rounded-2xl p-5 text-center ${
        darkMode
          ? 'bg-slate-800/50 border border-slate-700/50'
          : 'bg-white border border-gray-100 shadow-xl'
      }`}
    >
      <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-indigo-500" />
      <h4
        className={`font-bold text-base mb-2 ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}
      >
        {lang === 'hr' ? 'Online Check-in' : 'Online Check-in'}
      </h4>
      <p
        className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}
      >
        {lang === 'hr'
          ? 'Ispunite podatke za sve goste online — bez čekanja u redu.'
          : 'Fill in details for all guests online — no waiting in line.'}
      </p>
      <button
        className={`w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-600 shadow-lg`}
      >
        {lang === 'hr' ? 'Započni Check-in' : 'Start Check-in'}
      </button>
    </div>
  );
}

function DemoShareContent({ darkMode, lang }: { darkMode: boolean; lang: 'hr' | 'en' }) {
  return (
    <div
      className={`rounded-2xl p-5 text-center ${
        darkMode
          ? 'bg-slate-800/50 border border-slate-700/50'
          : 'bg-white border border-gray-100 shadow-xl'
      }`}
    >
      <Share2 className="w-12 h-12 mx-auto mb-3 text-sky-500" />
      <h4
        className={`font-bold text-base mb-2 ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}
      >
        {lang === 'hr' ? 'Podijeli s putnicima' : 'Share with travelers'}
      </h4>
      <p
        className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}
      >
        {lang === 'hr'
          ? `Pošalji link svim članovima grupe — svi imaju iste informacije o smještaju.`
          : 'Send the link to all group members — everyone gets the same info.'}
      </p>
      <div
        className={`p-3 rounded-xl text-xs font-mono mb-3 truncate ${
          darkMode ? 'bg-slate-900 text-cyan-400' : 'bg-gray-100 text-gray-700'
        }`}
      >
        bookiapp.com/guest/villa-ema-2026
      </div>
      <button
        className={`w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-sky-500 to-cyan-600 shadow-lg`}
      >
        {lang === 'hr' ? 'Kopiraj link' : 'Copy link'}
      </button>
    </div>
  );
}

// Re-export so caller can show host contact card too (uses same DEMO data)
export function DemoHostContact({
  darkMode,
  setShowChat,
}: {
  darkMode: boolean;
  setShowChat: (open: boolean) => void;
}) {
  return (
    <HostContactCard
      firstName={DEMO_LANDLORD.firstName}
      lastName={DEMO_LANDLORD.lastName}
      phone={DEMO_LANDLORD.phone}
      avatarUrl={DEMO_LANDLORD.avatarUrl}
      onChatClick={() => setShowChat(true)}
      darkMode={darkMode}
      isOnline={true}
      chatEnabled={true}
      responseTimeMinutes={12}
      emergencyContact={null}
    />
  );
}

// Cloud icon export used by parent for nothing — but keeps Sun unused warning
export const _DemoCloudIcon = Cloud;
